import { Component, type ErrorInfo, type ReactNode } from 'react';
import { recordClientError } from '../lib/clientErrorLog';
import { CriticalIcon } from './icons';

/** Props for {@link ErrorBoundary}. */
export interface ErrorBoundaryProps {
  /** The subtree to protect. */
  children: ReactNode;
  /** Where the boundary sits, for the log entry (e.g. the route path). */
  label?: string;
  /** Called when the user clicks "Try again" (e.g. to re-fetch). */
  onReset?: () => void;
}

interface ErrorBoundaryState {
  error: Error | null;
  componentStack: string | null;
  showDetails: boolean;
}

/**
 * Catches render-time crashes in its subtree and shows a recoverable fallback
 * instead of a white screen (the user's requirement: a view crash — an undefined
 * variant, a bad data shape — must never blank the page).
 *
 * On catch it records the error to {@link recordClientError} (console + in-memory
 * log) and renders a clear notice with "Try again" (reset + optional re-fetch),
 * "Reload page", and an expandable technical-details panel the operator can copy to
 * diagnose the issue live. Placed around the routed content and keyed by route, so
 * a crash is contained to the page while the shell/navigation stay usable, and it
 * clears automatically when the user navigates elsewhere.
 *
 * This must be a class component: only the class error-boundary lifecycle
 * (`getDerivedStateFromError` / `componentDidCatch`) can intercept a render crash.
 */
export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  public state: ErrorBoundaryState = { error: null, componentStack: null, showDetails: false };

  public static getDerivedStateFromError(error: Error): Partial<ErrorBoundaryState> {
    return { error };
  }

  public componentDidCatch(error: Error, info: ErrorInfo): void {
    const componentStack = info.componentStack ?? null;
    this.setState({ componentStack });
    recordClientError({
      source: 'render',
      message: error.message,
      stack: error.stack,
      componentStack: componentStack ?? undefined,
      path: this.props.label,
    });
  }

  private readonly handleReset = (): void => {
    this.setState({ error: null, componentStack: null, showDetails: false });
    this.props.onReset?.();
  };

  private readonly handleReload = (): void => {
    window.location.reload();
  };

  private readonly toggleDetails = (): void => {
    this.setState((current) => ({ showDetails: !current.showDetails }));
  };

  public render(): ReactNode {
    const { error, componentStack, showDetails } = this.state;
    if (error === null) {
      return this.props.children;
    }

    const details = `${error.message}\n\n${error.stack ?? ''}${componentStack ?? ''}`.trim();

    return (
      <div
        role="alert"
        className="mx-auto w-full max-w-2xl rounded-xl border border-red-200 bg-red-50/60 p-6 text-center shadow-sm dark:border-red-500/30 dark:bg-red-500/10"
      >
        <div className="mx-auto mb-3 flex h-11 w-11 items-center justify-center rounded-full bg-red-100 text-red-600 dark:bg-red-500/20 dark:text-red-300">
          <CriticalIcon size={22} />
        </div>
        <h2 className="text-lg font-semibold text-slate-900 dark:text-slate-100">Something went wrong on this page</h2>
        <p className="mx-auto mt-1 max-w-md text-sm text-slate-600 dark:text-slate-300">
          The rest of DeployHub is still working. You can try this page again, reload, or open the technical
          details to see what happened.
        </p>

        <div className="mt-4 flex flex-wrap items-center justify-center gap-2">
          <button
            type="button"
            onClick={this.handleReset}
            className="inline-flex items-center rounded-md bg-brand-500 px-3 py-2 text-sm font-medium text-white transition-colors hover:bg-brand-600 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-500"
          >
            Try again
          </button>
          <button
            type="button"
            onClick={this.handleReload}
            className="inline-flex items-center rounded-md border border-slate-300 px-3 py-2 text-sm font-medium text-slate-700 transition-colors hover:bg-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-500 dark:border-navy-border dark:text-slate-200 dark:hover:bg-navy-surface"
          >
            Reload page
          </button>
          <button
            type="button"
            onClick={this.toggleDetails}
            aria-expanded={showDetails}
            className="inline-flex items-center rounded-md px-3 py-2 text-sm font-medium text-slate-500 transition-colors hover:text-slate-800 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-500 dark:text-slate-400 dark:hover:text-slate-100"
          >
            {showDetails ? 'Hide details' : 'View details'}
          </button>
        </div>

        {showDetails ? (
          <pre className="mt-4 max-h-60 overflow-auto rounded-lg border border-slate-200 bg-white p-3 text-left text-xs text-slate-700 dark:border-navy-border dark:bg-navy-bg dark:text-slate-300">
            {details}
          </pre>
        ) : null}
      </div>
    );
  }
}
